<template>
    <RedesignBaseModal
        :visible="visibleDialog"
        :title="String($t('template.preview'))"
        :top="'8vh'"
        :destroy-on-close="true"
        append-to-body
        width="900px"
        wrapper-classes="full-height dashboard-widgets-modal"
        @update:visible="onChangeVisible"
        @close="closeModal"
    >
        <div class="w-full">
            <div
                v-if="selectedTemplate"
                class="mx-auto w-full max-w-2xl"
            >
                <div class="font-medium text-base mb-3 xl:mb-4">
                    {{ selectedTemplate.DashboardTemplateName }}
                </div>
                <div class="rounded p-3 xl:p-4 mb-5">
                    <div class="relative w-full border border-ui-lines rounded">
                        <LayoutPreview
                            :current-dashboard="dashboardPreviewData"
                            :default-zoom="0.4"
                            :min-zoom="0.4"
                            :header-caption="false"
                            class="aspect-video max-h-[400px]"
                        />
                    </div>
                </div>
                <div class="w-full mb-5">
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                            <div class="flex items-center gap-2">
                                <IconDashboardColor class="text-primary w-5 h-5" />
                                <span class="mx-1 main-text-color font-semibold">{{ $t('dashboard.colors') }}</span>
                            </div>
                            <div class="table px-3">
                                <div class="table-row-group">
                                    <div
                                        v-for="group in getGroupedParameters"
                                        :key="group.JPath"
                                        class="table-row"
                                    >
                                        <div class="table-cell align-middle py-1 px-2">
                                            <div class="flex items-center">
                                                {{ $t(group.LayoutParameterName) }}:
                                            </div>
                                        </div>
                                        <div class="table-cell align-middle py-1 px-2">
                                            <span class="flex items-center">
                                                <IconColorPicker
                                                    class="w-6 h-6"
                                                    :style="getIndicatorStyles(group)"
                                                />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <IconTimerSm class="text-primary w-5 h-5" />
                                <span class="mx-1 main-text-color font-semibold">{{ $t('widget.timers') }}</span>
                            </div>
                            <div class="px-3">
                                <div class="flex flex-col">
                                    <div
                                        class="flex items-center"
                                    >
                                        <div class="flex-grow break-keep py-1 px-2">
                                            <div class="flex items-center">
                                                {{ $t('dashboard.categorySwitching') }}:
                                            </div>
                                        </div>
                                        <div class="flex-shrink-0 py-1 px-2">
                                            <span class="flex items-center capitalize">
                                                {{ categorySwitching }}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        class="flex items-center"
                                    >
                                        <div class="flex-grow break-keep py-1 px-2">
                                            <div class="flex items-center">
                                                {{ $t('dashboard.categoryRefresh') }}:
                                            </div>
                                        </div>
                                        <div class="flex-shrink-0 py-1 px-2">
                                            <span class="flex items-center capitalize">
                                                {{ categoryRefresh }}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        class="flex items-center"
                                    >
                                        <div class="py-1 px-2 flex-grow break-keep">
                                            <div class="flex items-center">
                                                {{ $t('widget.minimumRefreshIntervalForRealTimeWidgets') }}:
                                            </div>
                                        </div>
                                        <div class="py-1 px-2 flex-shrink-0">
                                            <span class="flex items-center capitalize">
                                                {{ minRefreshInterval }}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        class="flex items-center"
                                    >
                                        <div class="py-1 px-2 flex-grow break-keep">
                                            <div class="flex items-center">
                                                {{ $t('dashboard.switchCategoryEvery') }}:
                                            </div>
                                        </div>
                                        <div class="py-1 px-2 flex-shrink-0">
                                            <span class="flex items-center capitalize">
                                                {{ reportInterval }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="!!widgetTemplateHelpData">
                    <div class="w-full border border-ui-lines rounded break-keep">
                        <div class="border-b border-ui-lines px-3 py-2 flex items-center justify-between gap-2">
                            {{ widgetTemplateHelpData?.TemplateName }}
                            <span
                                class="flex-center cursor-pointer text-active-elements hover:text-active-elements--focus"
                                @click="widgetTemplateHelpData = undefined"
                            >
                                <i class="vc-icon-close" />
                            </span>
                        </div>
                        <div class="w-full py-3">
                            <WidgetTemplateHelpInfo
                                :template-help="widgetTemplateHelpData"
                            />
                        </div>
                    </div>
                </div>
                <div
                    v-else
                    class="w-full space-y-2"
                >
                    <div
                        v-for="(widgetTemplate, index) in selectedTemplate.WidgetTemplateList"
                        :key="index"
                        class="w-full"
                    >
                        <TemplateWidget
                            :widget="widgetTemplate"
                            :template="selectedTemplate"
                            @learnMore="onDetailsView"
                        />
                    </div>
                </div>
            </div>
        </div>
    </RedesignBaseModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import { DashboardTemplateList } from '@/types/dashboard-template'
import LayoutPreview from '@/views/LayoutSettings/LayoutPreview.vue'
import { IDashboard, ILayoutParametersList, WidgetListExtended } from '@/types/dashboard'
import { DEFAULT_GROUP_KEYS } from '@/views/DashboardSettings/LayoutManagement/layout-management'
import { getPreviewLayout } from '@/composables/usePreviewLayout'
import { getActiveLayout } from '@/composables/globalData/useLayouts'
import TemplateWidget from '@/views/DashboardCreation/components/TemplateWidget.vue'
import WidgetTemplateHelpInfo from '@/components/RedesignComponents/Widgets/Common/WidgetTemplateHelpInfo.vue'
import { getHelpByWidgetsTemplateID } from '@/composables/globalData/useTemplatesDictionaryData'
import { TemplateHelp } from '@/types/template-help'
import i18n from '@/i18n' /* Data */

/* Data */
const { closeWithConfirm } = useConfirmBeforeClose()
const visibleDialog = ref(false)
const selectedTemplate = ref<DashboardTemplateList | undefined>(undefined)
const widgetTemplateHelpData = ref<TemplateHelp | undefined>(undefined)

/* Computed */
const activeLayout = computed(() => {
    return getPreviewLayout() || getActiveLayout()
})
const allWidgetTemplates = computed(() => {
    const templateList = selectedTemplate.value?.WidgetTemplateList || []
    return templateList.reduce<Array<WidgetListExtended>>((acc, item) => {
        if (!acc.some(obj => obj.WidgetTemplateID === item.WidgetTemplateID)) {
            acc.push({
                ...item,
                TemplateID: item.WidgetTemplateID,
                WidgetID: item.WidgetTemplateID,
                Title: item.DefaultWidgetTitle,
                WidgetConfig: item.DefaultWidgetConfig
            })
        }
        return acc

    }, [])
})
const dashboardPreviewData = computed(() => {
    return {
        WidgetGroupList: [
            {
                WidgetGroupID: Math.floor(Math.random() * 100),
                Order: 0,
                WidgetGroupTitle: i18n.t('common.newGroup'),
                WidgetList: allWidgetTemplates.value
            }
        ]
    } as IDashboard
})
const getGroupedParameters = computed<Array<ILayoutParametersList>>(() => {
    return activeLayout.value?.LayoutParametersList?.filter((el) => DEFAULT_GROUP_KEYS.Colors.includes(el.LayoutParameterName)) ?? []
})
const categorySwitching = computed<string>(() => {
    const ReportSwitching = activeLayout.value?.LayoutParametersList?.find((el) => el.LayoutParameterName === 'ReportSwitching')
    return ReportSwitching ? 'enabled' : 'disabled'
})
const categoryRefresh = computed<string>(() => {
    const ReportRefresh = activeLayout.value?.LayoutParametersList?.find((el) => el.LayoutParameterName === 'ReportRefresh')
    return ReportRefresh ? 'enabled' : 'disabled'
})
const minRefreshInterval = computed<string>(() => {
    const refreshInterval = activeLayout.value?.LayoutParametersList?.find((el) => el.LayoutParameterName === 'MinRefreshInterval')
    return `${refreshInterval?.Value || 0}s`
})
const reportInterval = computed<string>(() => {
    const interval = activeLayout.value?.LayoutParametersList?.find((el) => el.LayoutParameterName === 'ReportInterval')
    return `${interval?.Value || 0}s`
})

/* Methods */
function onChangeVisible (state: boolean) {
    visibleDialog.value = state
}

function openModal (templateData: DashboardTemplateList) {
    selectedTemplate.value = { ...templateData }
    visibleDialog.value = true
}

function forceCloseModal () {
    visibleDialog.value = false
    selectedTemplate.value = undefined
}

function closeModal () {
    closeWithConfirm(forceCloseModal)
}

function getIndicatorStyles (config: ILayoutParametersList) {
    return {
        color: config.Value
    }
}

function onDetailsView (widget: WidgetListExtended) {
    const templateID = widget.WidgetTemplateID
    widgetTemplateHelpData.value = getHelpByWidgetsTemplateID(templateID)
}

/* Exposed */
defineExpose({
    open: openModal,
    close: forceCloseModal
})
</script>
